import React from 'react';
import { Ban, Copy, ExternalLink, Gift, Pencil, Plus, RotateCw, ShieldAlert, ShieldCheck } from 'lucide-react';
import { Button } from '../../ui/button';
import { commentOf } from './ResultCardStrips';
import { overrideDefaults, overrideState, submitState } from './issue';

// The light "white" button of the design: the main action on red and neutral cards.
const LIGHT = 'bg-ink text-surface hover:bg-ink-dim';

function Row({ children }) {
  return <div className="flex flex-wrap items-center gap-3 border-t border-white/10 bg-surface-2/60 px-6 py-4">{children}</div>;
}

const Spacer = () => <span className="flex-1" />;

function BackOffice({ url }) {
  return (
    <Button asChild variant="outline" className="h-11 gap-2">
      <a href={url} target="_blank" rel="noopener noreferrer">Open back office <ExternalLink className="h-4 w-4" /></a>
    </Button>
  );
}

function IssueActions({ check, view, detailed, ui, patchUI, onCopy, onIssue, issuing }) {
  const issue = submitState(view.issue, ui.amountDraft);
  const canCopy = !view.wagering?.missing || view.wagering.value !== null;
  const canAdjust = check.proposal.issue_amount && !ui.adjusting;
  return (
    <Row>
      {view.kind === 'queued' && <CancelBonus ui={ui} patchUI={patchUI} />}
      {!detailed && !ui.commentOpen && view.kind !== 'queued' && (
        <Button variant="ghost" className="h-11 gap-2" onClick={() => patchUI({ commentOpen: true })}>
          <Plus className="h-4 w-4" /> Add comment
        </Button>
      )}
      <Spacer />
      {canAdjust && (
        <Button variant="outline" className="h-11 gap-2" onClick={() => patchUI({ adjusting: true })}>
          <Pencil className="h-4 w-4" /> Adjust amount
        </Button>
      )}
      <Button variant="outline" className="h-11 gap-2" disabled={!canCopy} onClick={onCopy}>
        <Copy className="h-4 w-4" /> Copy summary
      </Button>
      {!issue.canIssue && <span className="text-xs font-medium text-ink-mute">{issue.hint}</span>}
      <Button className="h-11 gap-2 px-5" disabled={!issue.canIssue || issuing} onClick={() => onIssue(commentOf(ui, check), null)}>
        <Gift className="h-4 w-4" />
        {issuing ? 'Saving…' : `${view.issue.label} · ${view.submitAmount}`}
      </Button>
    </Row>
  );
}

// CancelBonus withdraws a pending comp (it is then not issued); asks once before cancelling.
function CancelBonus({ ui, patchUI }) {
  return (
    <Button variant="ghost" className="h-11 gap-2 text-negative hover:text-negative" onClick={() => patchUI({ confirmCancel: true })}>
      <Ban className="h-4 w-4" /> Cancel bonus
    </Button>
  );
}

function ConfirmCancel({ check, patchUI, onCancel, cancelling }) {
  return (
    <Row>
      <span className="text-sm font-bold text-negative">Cancel this bonus? It will not be issued.</span>
      <Spacer />
      <Button variant="outline" className="h-11" onClick={() => patchUI({ confirmCancel: false })}>Keep it</Button>
      <Button className="h-11 gap-2 bg-negative text-white hover:bg-negative/90" disabled={cancelling}
        onClick={async () => { if (await onCancel(check.issued.id)) patchUI({ confirmCancel: false }); }}>
        <Ban className="h-4 w-4" /> {cancelling ? 'Cancelling…' : 'Yes, cancel bonus'}
      </Button>
    </Row>
  );
}

// IssueAnyway opens the override form on a failed check.
function IssueAnyway({ check, patchUI }) {
  return (
    <Button variant="outline" className="h-11 gap-2 border-warning/40 text-warning hover:text-warning" onClick={() => patchUI({ form: overrideDefaults(check) })}>
      <ShieldAlert className="h-4 w-4" /> Issue anyway
    </Button>
  );
}

function OverrideActions({ check, ui, patchUI, onIssue, issuing }) {
  const state = overrideState(ui.form);
  const label = check.issued?.status === 'pending' ? 'Update bonus' : 'Issue anyway';
  const amount = state.canIssue ? ` · ${ui.form.amount} ${ui.form.currency}` : '';
  return (
    <Row>
      <Button variant="ghost" className="h-11" onClick={() => patchUI({ form: null })}>Cancel</Button>
      <Spacer />
      {!state.canIssue && <span className="text-xs font-medium text-ink-mute">{state.hint}</span>}
      <Button className="h-11 gap-2 bg-warning px-5 text-surface hover:bg-warning/90" disabled={!state.canIssue || issuing}
        onClick={async () => { if (await onIssue(null, ui.form)) patchUI({ form: null }); }}>
        <Gift className="h-4 w-4" /> {issuing ? 'Saving…' : `${label}${amount}`}
      </Button>
    </Row>
  );
}

// CardActions: the action row of the result card; the main button sits bottom-right.
export default function CardActions(props) {
  const { check, view, ui, patchUI, setException, onCopy, onRecheck, backofficeUrl } = props;
  if (ui.form) return <OverrideActions {...props} />;
  if (ui.confirmCancel && check.issued?.status === 'pending') return <ConfirmCancel {...props} />;
  if (view.override && view.kind === 'queued') {
    return (
      <Row>
        <CancelBonus ui={ui} patchUI={patchUI} />
        <span className="text-sm font-medium text-ink-dim">Issued although a check failed. You can still correct it until it is pushed.</span>
        <Spacer />
        <Button variant="outline" className="h-11 gap-2" onClick={() => patchUI({ form: overrideDefaults(check) })}>
          <Pencil className="h-4 w-4" /> Edit bonus
        </Button>
      </Row>
    );
  }
  switch (view.kind) {
    case 'eligible':
    case 'granted':
    case 'manual':
    case 'queued':
      return <IssueActions {...props} />;
    case 'issued':
      return (
        <Row>
          <span className="text-sm font-medium text-ink-dim">One comp per period. This one is issued in the back office.</span>
          <Spacer />
          {check.proposal && <Button variant="outline" className="h-11 gap-2" onClick={onCopy}><Copy className="h-4 w-4" /> Copy summary</Button>}
          {view.bonusUrl && (
            <Button asChild className="h-11 gap-2">
              <a href={view.bonusUrl} target="_blank" rel="noopener noreferrer">Open bonus #{check.issued.issued_bonus_id} <ExternalLink className="h-4 w-4" /></a>
            </Button>
          )}
        </Row>
      );
    case 'pushed':
      return (
        <Row>
          <span className="text-sm font-medium text-ink-dim">One comp per period. This one has already gone out.</span>
          <Spacer />
          {check.proposal && <Button variant="outline" className="h-11 gap-2" onClick={onCopy}><Copy className="h-4 w-4" /> Copy summary</Button>}
          <Button className="h-11" disabled>Already pushed</Button>
        </Row>
      );
    case 'exception': {
      const reason = (ui.reason || '').trim();
      return (
        <Row>
          <button type="button" className="text-sm font-bold text-brand-blue hover:underline" onClick={() => patchUI({ guidance: !ui.guidance })}>
            {ui.guidance ? 'Hide guidelines' : 'Exception guidelines'}
          </button>
          <Spacer />
          <Button className="h-11 gap-2 bg-warning text-surface hover:bg-warning/90" disabled={!reason}
            title={reason ? undefined : 'Enter the reason first'} onClick={() => setException({ granted: true, reason })}>
            <ShieldCheck className="h-4 w-4" /> Grant exception
          </Button>
        </Row>
      );
    }
    case 'not_eligible':
      return (
        <Row>
          <BackOffice url={backofficeUrl} />
          <Spacer />
          {view.canOverride && <IssueAnyway check={check} patchUI={patchUI} />}
        </Row>
      );
    default:
      return (
        <Row>
          <BackOffice url={backofficeUrl} />
          <Spacer />
          {view.canOverride && <IssueAnyway check={check} patchUI={patchUI} />}
          <Button className={`h-11 gap-2 ${LIGHT}`} onClick={onRecheck}><RotateCw className="h-4 w-4" /> Run the check again</Button>
        </Row>
      );
  }
}
